import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { useCallback, useMemo, useState } from 'react'

import { Grid } from './Grid'
import { Nav } from './Nav'

const CURRENT_YEAR = new Date().getFullYear()

function isSameDate (date1?: Date | string | null, date2?: Date | string | null) {
  if (!date1 || !date2) return false

  const date1WithoutTime = new Date(date1).setHours(0, 0, 0, 0)
  const date2WithoutTime = new Date(date2).setHours(0, 0, 0, 0)

  return date1WithoutTime === date2WithoutTime
}

function dateFromProp (dateProp: Date | string | null | undefined) {
  if (dateProp instanceof Date || dateProp === null) {
    return dateProp
  }

  if (typeof dateProp === 'string') {
    const date = new Date(dateProp + 'T00:00:00')

    if (Number.isNaN(date.getTime())) {
      console.warn('Invalid date string "' + dateProp + '" passed to <Calendar />. Strings must be in the format "YYYY-MM-DD".')
    }

    return date
  }
}

interface CalendarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  /**
   * The selected date to display when the calendar is externally controlled.
   *
   * Can either be a `Date` object or a string in the format `YYYY-MM-DD`.
   **/
  date?: Date | string | null;
  /**
   * The initial date to display when the calendar is uncontrolled.
   *
   * Can either be a `Date` object or a string in the format `YYYY-MM-DD`.
   **/
  defaultDate?: Date | string;
  /**
   * The initial month to display when the calendar is uncontrolled. Like
   * Javascript `Date` objects, this value is zero-indexed (e.g. `0` is January).
   *
   * Defaults to the month of the selected date, or the current month if no
   * date has been selected.
   **/
  defaultMonth?: number;
  /**
   * The initial year to display when the calendar is uncontrolled.
   *
   * Defaults to the year of the selected date, or the current year if no
   * date has been selected.
   **/
  defaultYear?: number;
  /**
   * A list of dates to disable.
   *
   * Can be either 1) an array of `Date` objects or strings in the format `YYYY-MM-DD`,
   * or 2) a function that returns a boolean based on a `Date` object
   **/
  disabledDates?: Date[] | string[] | ((date: Date) => boolean);
  /**
   * The locale used to localize date strings in the calendar.
   *
   * Defaults to the browser's configured locale (`navigator.language`). Accepts
   * any valid BCP 47 language tag (e.g. `es-MX`).
   **/
  locale?: string;
  /**
   * The maximum date that can be selected. All days after this date will be
   * disabled, and the calendar will not allow the user to navigate to a month
   * after this date's month.
   *
   * Can either be a `Date` object or a string in the format `YYYY-MM-DD`.
   **/
  maxDate?: Date | string;
  /**
   * The maximum year that can be selected. The calendar will not allow the user
   * to navigate to a year after this year.
   *
   * Defaults to 10 years from the current year.
   **/
  maxYear?: number;
  /**
   * The minimum date that can be selected. All days before this date will be
   * disabled, and the calendar will not allow the user to navigate to a month
   * before this date's month.
   *
   * Can either be a `Date` object or a string in the format `YYYY-MM-DD`.
   **/
  minDate?: Date | string;
  /**
   * The minimum year that can be selected. The calendar will not allow the user
   * to navigate to a year before this year.
   *
   * Defaults to 100 years before the current year.
   **/
  minYear?: number;
  /**
   * The month to display when the calendar is externally controlled. Like
   * Javascript `Date` objects, this value is zero-indexed (e.g. `0` is January).
   **/
  month?: number;
  /**
   * A callback function that will be called when the selected date changes.
   *
   * Will be called with a `Date` object.
   **/
  onDateChange?: (date: Date | null) => void;
  /**
   * A callback function that will be called when the displayed month changes.
   *
   * Will be called with a zero-indexed month (e.g. `0` is January).
   **/
  onMonthChange?: (month: number) => void;
  /**
   * A callback function that will be called when the displayed year changes.
   *
   * Will be called with a year.
   **/
  onYearChange?: (year: number) => void;
  /**
   * The year to display when the calendar is externally controlled.
   **/
  year?: number;
}

export function Calendar ({
  date: dateProp,
  defaultDate,
  defaultMonth,
  defaultYear,
  disabledDates,
  locale = navigator.language ?? 'en-US',
  maxDate: maxDateProp,
  maxYear = CURRENT_YEAR + 10,
  minDate: minDateProp,
  minYear = CURRENT_YEAR - 100,
  month: monthProp,
  onDateChange,
  onMonthChange,
  onYearChange,
  year: yearProp,
  ...props
}: CalendarProps) {
  const minDate = useMemo(() => dateFromProp(minDateProp), [minDateProp])
  const maxDate = useMemo(() => dateFromProp(maxDateProp), [maxDateProp])
  const initialDate = useMemo(
    () => dateFromProp(dateProp ?? defaultDate) ?? new Date(),
    [dateProp, defaultDate]
  )

  const [focusedDate, setFocusedDate] = useState(() => initialDate.getDate())

  const [date, setDate] = useControllableState({
    prop: dateFromProp(dateProp),
    defaultProp: dateFromProp(defaultDate) || null,
    onChange: onDateChange,
  })
  const [month, setMonth] = useControllableState({
    prop: monthProp,
    defaultProp: defaultMonth ?? initialDate.getMonth(),
    onChange: onMonthChange,
  })
  const [year, setYear] = useControllableState({
    prop: yearProp,
    defaultProp: defaultYear ?? initialDate.getFullYear(),
    onChange: onYearChange,
  })

  const isDateDisabled = useCallback((date?: Date) => {
    if (!date) return true
    if (minDate && date < minDate) return true
    if (maxDate && date > maxDate) return true

    if (typeof disabledDates === 'function') {
      return disabledDates(date)
    }

    if (Array.isArray(disabledDates)) {
      return disabledDates.some((disabledDate) => isSameDate(dateFromProp(disabledDate), date))
    }

    return false
  }, [minDate, maxDate, disabledDates])

  return (
    <div {...props} className='inline-flex flex-col'>
      <Nav
        locale={locale}
        maxDate={maxDate}
        maxYear={maxYear}
        minDate={minDate}
        minYear={minYear}
        month={month}
        setFocusedDate={setFocusedDate}
        setMonth={setMonth}
        setYear={setYear}
        year={year}
      />
      <Grid
        date={date}
        focusedDate={focusedDate}
        isDateDisabled={isDateDisabled}
        locale={locale}
        maxDate={maxDate}
        maxYear={maxYear}
        minYear={minYear}
        minDate={minDate}
        month={month}
        setDate={setDate}
        setFocusedDate={setFocusedDate}
        setMonth={setMonth}
        setYear={setYear}
        year={year}
      />
    </div>
  )
}
